<template>
  <div>
    <Head :title="`Editar producto - ${form.name}`" />
    
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div class="flex items-center gap-4">
        <button 
          class="p-3 text-white bg-teal-700 hover:bg-teal-800 rounded-lg transition-colors"
          title="Volver atrás"
          @click="back()"
        >
          <i class="fa fa-arrow-left"></i>
        </button>
        <div>
          <h1 class="text-2xl font-bold flex items-center gap-2">
            <Link 
              class="text-teal-600 hover:text-teal-700 transition-colors" 
              href="/products"
            >
              Productos
            </Link>
            <span class="text-gray-400 font-medium">/</span>
            <span class="text-gray-800">{{ form.name }}</span>
            <span class="text-xs font-mono bg-gray-100 text-gray-700 px-2 py-0.5 rounded border border-gray-200">
              {{ form.code }}
            </span>
          </h1>
          <div class="flex items-center gap-2 mt-1">
            <!-- Stock Status Badge -->
            <span 
              v-if="product.stock_status === 'out_of_stock'"
              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-800"
            >
              <i class="fa fa-ban text-[10px]"></i> Sin Stock (0)
            </span>
            <span 
              v-else-if="product.stock_status === 'low_stock'"
              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-900 border border-amber-300"
            >
              <i class="fa fa-exclamation-triangle text-[10px]"></i> Stock Bajo: {{ product.quantity }} (Mín: {{ product.min_stock }})
            </span>
            <span 
              v-else-if="product.stock_status === 'excess_stock'"
              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-purple-100 text-purple-900 border border-purple-300"
            >
              <i class="fa fa-layer-group text-[10px]"></i> Exceso: {{ product.quantity }} (Máx: {{ product.max_stock }})
            </span>
            <span 
              v-else
              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800"
            >
              <i class="fa fa-check-circle text-[10px]"></i> Stock Normal: {{ product.quantity }}
            </span>

            <span v-if="product.cross_references?.length > 0" class="text-xs text-indigo-700 font-medium">
              • {{ product.cross_references.length }} referencias cruzadas
            </span>
          </div>
        </div>
      </div>

      <!-- Action Buttons Header -->
      <div class="flex items-center gap-3">
        <button 
          type="button"
          @click="openBarcodeModal"
          class="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg shadow-sm transition-all shadow-indigo-600/20"
          title="Ver e Imprimir Código de Barras en Tickera"
        >
          <i class="fa fa-barcode text-base"></i>
          <span>Código de Barras / Tickera</span>
        </button>
      </div>
    </div>

    <!-- Navigation Tabs -->
    <div class="flex border-b border-gray-200 mb-6 bg-white rounded-t-lg px-4 pt-2 shadow-sm">
      <button
        type="button"
        @click="activeTab = 'general'"
        class="py-3 px-5 text-sm font-semibold border-b-2 flex items-center gap-2 transition-colors"
        :class="activeTab === 'general' ? 'border-teal-600 text-teal-700 bg-teal-50/50 rounded-t-md' : 'border-transparent text-gray-500 hover:text-gray-700'"
      >
        <i class="fa fa-sliders"></i>
        <span>Información & Precios</span>
      </button>

      <button
        type="button"
        @click="activeTab = 'history'"
        class="py-3 px-5 text-sm font-semibold border-b-2 flex items-center gap-2 transition-colors"
        :class="activeTab === 'history' ? 'border-teal-600 text-teal-700 bg-teal-50/50 rounded-t-md' : 'border-transparent text-gray-500 hover:text-gray-700'"
      >
        <i class="fa fa-history"></i>
        <span>Historial de Movimientos / Kardex</span>
        <span v-if="history?.total_events" class="text-xs px-2 py-0.2 rounded-full bg-blue-100 text-blue-800 font-bold">
          {{ history.total_events }}
        </span>
      </button>

      <button
        type="button"
        @click="activeTab = 'cross_references'"
        class="py-3 px-5 text-sm font-semibold border-b-2 flex items-center gap-2 transition-colors"
        :class="activeTab === 'cross_references' ? 'border-teal-600 text-teal-700 bg-teal-50/50 rounded-t-md' : 'border-transparent text-gray-500 hover:text-gray-700'"
      >
        <i class="fa fa-exchange-alt"></i>
        <span>Referencias Cruzadas</span>
        <span v-if="product.cross_references?.length" class="text-xs px-2 py-0.2 rounded-full bg-indigo-100 text-indigo-800 font-bold">
          {{ product.cross_references.length }}
        </span>
      </button>

      <button
        type="button"
        @click="activeTab = 'imeis'"
        class="py-3 px-5 text-sm font-semibold border-b-2 flex items-center gap-2 transition-colors"
        :class="activeTab === 'imeis' ? 'border-teal-600 text-teal-700 bg-teal-50/50 rounded-t-md' : 'border-transparent text-gray-500 hover:text-gray-700'"
      >
        <i class="fa fa-barcode"></i>
        <span>IMEIs / Seriales</span>
        <span v-if="product.imeis?.length" class="text-xs px-2 py-0.2 rounded-full bg-gray-100 text-gray-700 font-bold">
          {{ product.imeis.length }}
        </span>
      </button>
    </div>

    <!-- TAB 1: INFORMACIÓN GENERAL Y PRECIOS -->
    <div v-show="activeTab === 'general'" class="max-w-4xl bg-white rounded-lg shadow-sm border border-gray-200">
      <form @submit.prevent="update">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 p-8">
          <!-- Basic Info -->
          <div class="lg:col-span-2">
            <h2 class="text-lg font-bold mb-4 text-gray-800 flex items-center gap-2">
              <i class="fa fa-info-circle text-teal-600"></i>
              <span>Información básica</span>
            </h2>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div class="relative">
                <text-input
                  v-model="form.code"
                  :error="form.errors.code"
                  label="Código *"
                  placeholder="Ingrese el código del producto"
                />
                <button
                  type="button"
                  @click="openBarcodeModal"
                  class="absolute top-8 right-2.5 text-indigo-600 hover:text-indigo-800 p-1 rounded hover:bg-indigo-50 transition-colors"
                  title="Ver e imprimir código de barras en tickera"
                >
                  <i class="fa fa-barcode text-lg"></i>
                </button>
              </div>
              <text-input
                v-model="form.name"
                :error="form.errors.name"
                label="Nombre *"
                placeholder="Ingrese el nombre del producto" 
              />
              <text-input
                v-model="form.description"
                :error="form.errors.description"
                label="Descripción"
                placeholder="Ingrese una descripción"
                class="lg:col-span-2"
              />
            </div>
          </div>

          <!-- Stock Limits & Tags -->
          <div class="lg:col-span-2 p-5 bg-blue-50/30 rounded-xl border border-blue-100">
            <h2 class="text-lg font-bold mb-3 text-blue-900 flex items-center gap-2">
              <i class="fa fa-bell text-blue-600"></i>
              <span>Límites de Stock & Etiquetas</span>
            </h2>
            <p class="text-xs text-gray-500 mb-4">
              Configure los umbrales de advertencia para saber cuándo un producto está por agotarse o sobrepasa el límite óptimo en almacén.
            </p>
            <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <text-input
                v-model.number="form.min_stock"
                :error="form.errors.min_stock"
                type="number"
                min="0"
                label="Stock Mínimo (Alerta de escasez)"
                placeholder="0"
              />
              <text-input
                v-model.number="form.max_stock"
                :error="form.errors.max_stock"
                type="number"
                min="0"
                label="Stock Máximo (Alerta de sobre-stock)"
                placeholder="0"
              />
              <text-input
                v-model="form.tags"
                :error="form.errors.tags"
                label="Etiquetas (separadas por coma)"
                placeholder="ej. repuesto, motor, original, oferta"
              />
            </div>
          </div>

          <!-- Administrar Planes de Recarga (en Bs) si es producto de planes -->
          <div v-if="isPlanProduct(form.code)" class="lg:col-span-2 bg-blue-50/70 border-2 border-blue-200 rounded-xl p-5 mb-2 shadow-sm">
            <div class="flex items-center justify-between mb-4 pb-2 border-b border-blue-200">
              <div>
                <h2 class="text-base font-bold text-blue-900 flex items-center gap-2">
                  <i class="fa fa-cogs text-blue-600"></i>
                  <span>Administrar Planes y Tarifas (Bs.)</span>
                </h2>
                <p class="text-xs text-blue-700 mt-0.5">
                  Gestiona los planes que aparecerán en el selector del POS. Las tarifas se registran en Bolívares (Bs.).
                </p>
              </div>
              <button 
                type="button" 
                @click="addProductPlanRow" 
                class="px-3.5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 shadow-sm transition-colors cursor-pointer"
              >
                <i class="fa fa-plus-circle text-xs"></i>
                <span>+ Añadir Nuevo Plan</span>
              </button>
            </div>

            <div class="space-y-3">
              <div 
                v-for="(plan, pIdx) in productPlans" 
                :key="pIdx"
                class="flex items-center gap-3 bg-white p-3 rounded-lg border border-blue-200 shadow-sm"
              >
                <div class="w-1/2">
                  <label class="block text-xs font-semibold text-gray-700 mb-1">Nombre del Plan</label>
                  <input 
                    type="text" 
                    v-model="plan.name" 
                    placeholder="Ej: Plan 6GB" 
                    class="w-full text-sm font-medium border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div class="w-2/5">
                  <label class="block text-xs font-semibold text-gray-700 mb-1">Tarifa en Bs.</label>
                  <div class="flex items-center gap-1.5">
                    <span class="text-sm font-bold text-blue-700">Bs.</span>
                    <input 
                      type="number" 
                      step="any" 
                      min="0" 
                      v-model.number="plan.price" 
                      placeholder="0.00" 
                      class="w-full text-sm font-bold text-right border-gray-300 rounded-md px-3 py-2 text-blue-900 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div class="w-1/10 pt-5 flex justify-center">
                  <button 
                    type="button" 
                    @click="removeProductPlanRow(pIdx)" 
                    class="text-red-400 hover:text-red-600 p-1.5 rounded transition-colors cursor-pointer"
                    title="Eliminar este plan"
                  >
                    <i class="fa fa-trash-alt text-sm"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Prices -->
          <div class="lg:col-span-2">
            <h2 class="text-lg font-bold mb-4 text-gray-800 flex items-center gap-2">
              <i class="fa fa-dollar-sign text-teal-600"></i>
              <span>Precios Generales</span>
            </h2>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <text-input
                v-model="form.cost"
                :error="form.errors.cost"
                label="Costo *"
                type="number"
                step="0.01"
                placeholder="Ingrese el costo"
              />
              <text-input
                v-for="(group, key) in groups"
                :key="group.id"
                v-model.number="form.prices[key]"
                type="number"
                :error="form.errors.prices"
                :label="`Precio ${group.name} *`"
                step="0.01"
                placeholder="Ingrese el precio"
              />
            </div>
          </div>

          <!-- Categories -->
          <div class="lg:col-span-2">
            <h2 class="text-lg font-bold mb-4 text-gray-800 flex items-center gap-2">
              <i class="fa fa-tags text-teal-600"></i>
              <span>Categorización y Propiedades</span>
            </h2>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <select-input
                v-model="form.brand_id"
                :error="form.errors.brand_id"
                label="Marca *"
              >
                <option value="">Seleccionar marca</option>
                <option
                  v-for="brand in brands"
                  :key="brand.id"
                  :value="brand.id"
                >
                  {{ brand.name }}
                </option>
              </select-input>

              <select-input
                v-model="form.product_category_id"
                :error="form.errors.product_category_id"
                label="Categoría *"
              >
                <option value="">Seleccionar categoría</option>
                <option
                  v-for="category in productCategories"
                  :key="category.id"
                  :value="category.id"
                >
                  {{ category.name }}
                </option>
              </select-input>

              <select-input
                v-model="form.contable"
                :error="form.errors.contable"
                label="Contable *"
              >
                <option value="">Seleccionar opción</option>
                <option value="0">No</option>
                <option value="1">Sí</option>
              </select-input>

              <select-input
                v-model="form.has_imei"
                :error="form.errors.has_imei"
                label="¿Requiere IMEI? *"
              >
                <option :value="false">No</option>
                <option :value="true">Sí</option>
              </select-input>

              <select-input
                v-model="form.require_note"
                :error="form.errors.require_note"
                label="¿Requiere nota? *"
              >
                <option :value="false">No</option>
                <option :value="true">Sí</option>
              </select-input>

              <file-input
                v-model="form.photo"
                :error="form.errors.photo"
                type="file"
                accept="image/*"
                label="Foto del producto"
              />
            </div>
          </div>

          <!-- Commission -->
          <div class="lg:col-span-2">
            <h2 class="text-lg font-bold mb-4 text-gray-800 flex items-center gap-2">
              <i class="fa fa-percentage text-teal-600"></i>
              <span>Comisiones</span>
            </h2>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <select-input
                v-model="form.commission_type"
                :error="form.errors.commission_type"
                label="Tipo de Comisión"
              >
                <option value="">Sin Comisión</option>
                <option value="percentage">Porcentaje (%)</option>
                <option value="fixed">Monto Fijo</option>
              </select-input>

              <text-input
                v-if="form.commission_type"
                v-model="form.commission_value"
                :error="form.errors.commission_value"
                type="number"
                step="0.01"
                min="0"
                label="Valor de la Comisión"
                placeholder="Ingrese el valor"
              />
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex items-center justify-between px-8 py-4 bg-gray-50 border-t">
          <button
            type="button"
            class="text-red-600 hover:text-red-700 font-medium transition-colors"
            @click="destroy"
          >
            Eliminar producto
          </button>
          
          <loading-button
            :loading="form.processing"
            class="btn-blue"
          >
            Actualizar producto
          </loading-button>
        </div>
      </form>
    </div>

    <!-- TAB 2: HISTORIAL DE MOVIMIENTOS / KARDEX -->
    <div v-show="activeTab === 'history'" class="space-y-6">
      <!-- Metrics Cards -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <!-- Stock Actual -->
        <div class="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
          <div class="flex items-center justify-between">
            <span class="text-xs font-semibold text-gray-500 uppercase">Stock Actual</span>
            <div class="w-8 h-8 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center">
              <i class="fa fa-boxes"></i>
            </div>
          </div>
          <div class="text-2xl font-bold text-gray-900 mt-2">
            {{ history?.metrics?.current_stock || product.quantity }} uds
          </div>
          <div class="text-xs text-gray-500 mt-1 flex items-center gap-1">
            <span>Mín: {{ history?.metrics?.min_stock || 0 }}</span>
            <span>•</span>
            <span>Máx: {{ history?.metrics?.max_stock || 0 }}</span>
          </div>
        </div>

        <!-- Total Comprado -->
        <div class="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
          <div class="flex items-center justify-between">
            <span class="text-xs font-semibold text-gray-500 uppercase">Total Comprado (Entradas)</span>
            <div class="w-8 h-8 rounded-lg bg-green-50 text-green-600 flex items-center justify-center">
              <i class="fa fa-shopping-cart"></i>
            </div>
          </div>
          <div class="text-2xl font-bold text-green-700 mt-2">
            +{{ history?.metrics?.total_purchased_quantity || 0 }} uds
          </div>
          <div class="text-xs text-gray-500 mt-1">
            Total invertido: {{ formatCurrency(history?.metrics?.total_purchased_amount || 0) }}
          </div>
        </div>

        <!-- Total Vendido -->
        <div class="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
          <div class="flex items-center justify-between">
            <span class="text-xs font-semibold text-gray-500 uppercase">Total Vendido (Salidas)</span>
            <div class="w-8 h-8 rounded-lg bg-red-50 text-red-600 flex items-center justify-center">
              <i class="fa fa-dollar-sign"></i>
            </div>
          </div>
          <div class="text-2xl font-bold text-red-700 mt-2">
            -{{ history?.metrics?.total_sold_quantity || 0 }} uds
          </div>
          <div class="text-xs text-gray-500 mt-1">
            Facturación: {{ formatCurrency(history?.metrics?.total_sold_amount || 0) }}
          </div>
        </div>

        <!-- Retiros / Ajustes -->
        <div class="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
          <div class="flex items-center justify-between">
            <span class="text-xs font-semibold text-gray-500 uppercase">Ajustes de Inventario</span>
            <div class="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
              <i class="fa fa-clipboard-list"></i>
            </div>
          </div>
          <div class="text-lg font-bold text-gray-800 mt-2">
            +{{ history?.metrics?.total_inventory_added || 0 }} / -{{ history?.metrics?.total_inventory_removed || 0 }}
          </div>
          <div class="text-xs text-gray-500 mt-1">
            Entradas manuales vs retiros
          </div>
        </div>
      </div>

      <!-- History Table Container -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <!-- Filter and Search Header -->
        <div class="p-4 bg-gray-50 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
          <div class="flex flex-wrap items-center gap-2">
            <button
              v-for="btn in historyFilterButtons"
              :key="btn.value"
              @click="historyFilterType = btn.value"
              class="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
              :class="historyFilterType === btn.value ? 'bg-teal-700 text-white shadow-sm' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'"
            >
              {{ btn.label }}
            </button>
          </div>

          <div class="w-full sm:w-64">
            <div class="relative">
              <input
                v-model="historySearch"
                type="text"
                class="form-input w-full rounded-lg border-gray-300 text-xs pl-8 focus:border-teal-500 focus:ring-teal-500"
                placeholder="Buscar cliente, proveedor, factura..."
              />
              <i class="fa fa-search absolute left-2.5 top-2.5 text-gray-400 text-xs"></i>
            </div>
          </div>
        </div>

        <!-- Events List -->
        <div class="overflow-x-auto">
          <table class="w-full whitespace-nowrap text-sm">
            <thead>
              <tr class="text-left bg-gray-50 text-gray-500 text-xs font-semibold uppercase tracking-wider border-b border-gray-200">
                <th class="py-3 px-4">Fecha / Hora</th>
                <th class="py-3 px-4">Tipo Movimiento</th>
                <th class="py-3 px-4 text-center">Cantidad</th>
                <th class="py-3 px-4">Tercero Involucrado (A quién / De quién)</th>
                <th class="py-3 px-4">Referencia / Folio</th>
                <th class="py-3 px-4">Depósito / Tienda</th>
                <th class="py-3 px-4 text-right">Precio / Costo Unit.</th>
                <th class="py-3 px-4 text-right">Monto Total</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr 
                v-for="evt in filteredHistoryEvents" 
                :key="evt.id"
                class="hover:bg-gray-50 transition-colors"
              >
                <!-- Fecha -->
                <td class="py-3 px-4 text-gray-600 font-mono text-xs">
                  {{ evt.date_formatted }}
                </td>

                <!-- Tipo -->
                <td class="py-3 px-4">
                  <span 
                    :class="[
                      'inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold uppercase',
                      evt.type === 'purchase' ? 'bg-green-100 text-green-800' :
                      evt.type === 'sale' ? 'bg-red-100 text-red-800' :
                      evt.type === 'inventory_in' ? 'bg-teal-100 text-teal-800' :
                      evt.type === 'inventory_out' ? 'bg-amber-100 text-amber-800' :
                      evt.type === 'transfer' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-700'
                    ]"
                  >
                    <i 
                      :class="[
                        evt.type === 'purchase' ? 'fa fa-arrow-down' :
                        evt.type === 'sale' ? 'fa fa-arrow-up' :
                        evt.type === 'inventory_in' ? 'fa fa-plus-circle' :
                        evt.type === 'inventory_out' ? 'fa fa-minus-circle' :
                        evt.type === 'transfer' ? 'fa fa-exchange-alt' :
                        'fa fa-check'
                      ]"
                      class="text-[10px]"
                    ></i>
                    {{ evt.type_label }}
                  </span>
                </td>

                <!-- Cantidad -->
                <td class="py-3 px-4 text-center font-bold font-mono">
                  <span 
                    :class="[
                      evt.quantity > 0 ? 'text-green-700' :
                      evt.quantity < 0 ? 'text-red-700' :
                      'text-gray-500'
                    ]"
                  >
                    {{ evt.quantity > 0 ? `+${evt.quantity}` : evt.quantity }} uds
                  </span>
                </td>

                <!-- Tercero Involucrado (Cliente a quien se vendió / Proveedor / Usuario) -->
                <td class="py-3 px-4">
                  <div class="flex flex-col">
                    <span class="font-medium text-gray-900">
                      {{ evt.entity_name }}
                    </span>
                    <span v-if="evt.entity_detail" class="text-xs text-gray-500 font-mono">
                      {{ evt.entity_type }}: {{ evt.entity_detail }}
                    </span>
                    <span v-if="evt.user_name" class="text-[11px] text-gray-400">
                      Por: {{ evt.user_name }}
                    </span>
                  </div>
                </td>

                <!-- Referencia -->
                <td class="py-3 px-4">
                  <span class="text-xs font-semibold text-gray-800">
                    {{ evt.description }}
                  </span>
                </td>

                <!-- Depósito -->
                <td class="py-3 px-4 text-xs text-gray-600">
                  <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-100 text-gray-700 font-medium">
                    <i class="fa fa-warehouse text-[10px] text-gray-400"></i>
                    {{ evt.deposit_name }}
                  </span>
                </td>

                <!-- Precio / Costo -->
                <td class="py-3 px-4 text-right font-mono text-xs text-gray-700">
                  {{ formatCurrency(evt.unit_price) }}
                </td>

                <!-- Total -->
                <td class="py-3 px-4 text-right font-mono font-bold text-xs" :class="evt.quantity < 0 ? 'text-gray-800' : 'text-gray-800'">
                  {{ formatCurrency(evt.total_amount) }}
                </td>
              </tr>

              <tr v-if="!filteredHistoryEvents.length">
                <td colspan="8" class="py-8 text-center text-gray-400">
                  <div class="flex flex-col items-center justify-center gap-2">
                    <i class="fa fa-folder-open text-3xl text-gray-300"></i>
                    <span>No hay movimientos registrados para este producto</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- TAB 3: REFERENCIAS CRUZADAS (CROSS-REFERENCES) -->
    <div v-show="activeTab === 'cross_references'" class="space-y-6">
      <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <!-- Header -->
        <div class="p-6 border-b border-gray-100 flex flex-wrap justify-between items-center bg-gray-50 gap-4">
          <div>
            <h2 class="text-lg font-bold text-gray-900 flex items-center gap-2">
              <i class="fa fa-exchange-alt text-indigo-600"></i>
              <span>Referencias Cruzadas de este Producto</span>
            </h2>
            <p class="text-xs text-gray-500 mt-1">
              Asocie códigos de fabricante original (OEM), números de parte alternativos o repuestos equivalentes.
              Al buscar por cualquiera de estos códigos en el buscador, este producto será localizado.
            </p>
          </div>
          <button
            type="button"
            @click="openCrossReferenceModal"
            class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-semibold flex items-center gap-2 transition-colors"
          >
            <i class="fa fa-plus"></i>
            <span>Agregar Referencia</span>
          </button>
        </div>

        <!-- Table -->
        <div class="overflow-x-auto">
          <table class="w-full whitespace-nowrap text-sm">
            <thead>
              <tr class="text-left bg-gray-50 text-gray-500 text-xs font-semibold uppercase tracking-wider border-b border-gray-200">
                <th class="py-3 px-6">Código Alterno / OEM</th>
                <th class="py-3 px-6">Marca / Fabricante</th>
                <th class="py-3 px-6">Descripción / Notas de Equivalencia</th>
                <th class="py-3 px-6">Producto del Catálogo Vinculado</th>
                <th class="py-3 px-6 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr 
                v-for="ref in (product.cross_references || [])" 
                :key="ref.id"
                class="hover:bg-gray-50"
              >
                <td class="py-4 px-6 font-mono font-bold text-indigo-900 text-sm">
                  {{ ref.code }}
                </td>
                <td class="py-4 px-6 text-gray-700 font-medium">
                  <span class="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-800 text-xs font-semibold">
                    {{ ref.brand || 'OEM / Genérico' }}
                  </span>
                </td>
                <td class="py-4 px-6 text-gray-600 text-xs max-w-xs truncate">
                  {{ ref.description || '-' }}
                </td>
                <td class="py-4 px-6 text-sm">
                  <Link 
                    v-if="ref.cross_product" 
                    :href="`/products/${ref.cross_product.id}`"
                    class="text-blue-600 hover:underline font-medium flex items-center gap-1.5"
                  >
                    <i class="fa fa-link text-xs"></i>
                    <span>{{ ref.cross_product.name }} ({{ ref.cross_product.code }})</span>
                  </Link>
                  <span v-else class="text-gray-400 text-xs">-</span>
                </td>
                <td class="py-4 px-6 text-center">
                  <button
                    type="button"
                    @click="deleteCrossReference(ref)"
                    class="p-1.5 text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 rounded transition-colors"
                    title="Eliminar referencia cruzada"
                  >
                    <i class="fa fa-trash"></i>
                  </button>
                </td>
              </tr>

              <tr v-if="!product.cross_references || product.cross_references.length === 0">
                <td colspan="5" class="py-8 text-center text-gray-400 text-sm">
                  <div class="flex flex-col items-center justify-center gap-2">
                    <i class="fa fa-barcode text-3xl text-gray-300"></i>
                    <span>No hay referencias cruzadas asociadas a este producto aún</span>
                    <button 
                      type="button" 
                      @click="openCrossReferenceModal"
                      class="text-indigo-600 hover:underline text-xs font-semibold mt-1"
                    >
                      + Asociar primer código alterno
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- TAB 4: IMEIs / SERIALES -->
    <div v-show="activeTab === 'imeis'" class="max-w-4xl bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div class="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
        <div>
          <h2 class="text-lg font-bold text-gray-800 flex items-center gap-2">
            <i class="fa fa-barcode text-indigo-600"></i>
            <span>Equipos / IMEIs Asignados a este Producto</span>
          </h2>
          <p class="text-xs text-gray-500 mt-0.5">Seguimiento de seriales e inventario por tienda</p>
        </div>
        <div class="flex items-center gap-3">
          <span v-if="product.imeis" class="px-2.5 py-1 text-xs font-bold bg-indigo-100 text-indigo-800 rounded-full">
            {{ product.imeis.length }} IMEIs
          </span>
        </div>
      </div>

      <!-- Missing IMEIs Alert -->
      <div v-if="missingImeisPerDeposit.length > 0" class="p-4 bg-yellow-50 border-b border-yellow-100">
        <div class="flex flex-col gap-3">
          <div class="flex items-center gap-2 text-yellow-800 font-semibold">
            <i class="fa fa-exclamation-triangle"></i>
            <span>Existen ítems en inventario sin IMEI asignado</span>
          </div>
          <div class="grid gap-2">
            <div v-for="item in missingImeisPerDeposit" :key="item.deposit.id" class="flex items-center justify-between bg-white p-3 rounded shadow-sm border border-yellow-200">
              <div class="text-sm">
                <span class="font-bold">{{ item.deposit.name }}:</span> 
                Faltan {{ item.missing }} IMEIs (Inventario: {{ item.quantity }}, Asignados: {{ item.availableImeis }})
              </div>
              <button 
                type="button"
                @click="openMissingImeisModal(item)"
                class="px-3 py-1.5 text-xs font-bold text-white bg-yellow-600 hover:bg-yellow-700 rounded transition-colors"
              >
                Asignar IMEIs
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full whitespace-nowrap">
          <thead>
            <tr class="text-left bg-gray-50 text-gray-500 text-xs font-semibold uppercase tracking-wider">
              <th class="py-3 px-6">IMEI</th>
              <th class="py-3 px-6">Tienda / Depósito</th>
              <th class="py-3 px-6">Proveedor</th>
              <th class="py-3 px-6">Factura</th>
              <th class="py-3 px-6 text-center">Estado</th>
              <th class="py-3 px-6 text-center">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 text-sm">
            <tr v-for="imeiItem in (product.imeis || [])" :key="imeiItem.id" class="hover:bg-gray-50">
              <td class="py-3 px-6 font-mono font-bold text-indigo-900">
                {{ imeiItem.imei }}
              </td>
              <td class="py-3 px-6">
                <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-gray-100 text-gray-700">
                  <i class="fa fa-store text-gray-400"></i>
                  <span>{{ imeiItem.deposit ? imeiItem.deposit.name : 'Sin Tienda' }}</span>
                </span>
              </td>
              <td class="py-3 px-6 text-gray-600">
                {{ imeiItem.provider ? imeiItem.provider.name : '-' }}
              </td>
              <td class="py-3 px-6 text-gray-600">
                {{ imeiItem.invoice_number || '-' }}
              </td>
              <td class="py-3 px-6 text-center">
                <span 
                  :class="[
                    'px-2 py-0.5 text-xs font-bold rounded-full uppercase',
                    imeiItem.status === 'available' ? 'bg-green-100 text-green-800' :
                    imeiItem.status === 'transferred' ? 'bg-blue-100 text-blue-800' : 
                    imeiItem.status === 'sold' ? 'bg-purple-100 text-purple-800' :
                    'bg-gray-100 text-gray-800'
                  ]"
                >
                  {{ imeiItem.status === 'available' ? 'Disponible' : imeiItem.status === 'transferred' ? 'Trasladado' : imeiItem.status === 'sold' ? 'Venta' : imeiItem.status }}
                </span>
              </td>
              <td class="py-3 px-6 text-center whitespace-nowrap">
                <div class="flex items-center justify-center gap-2">
                  <button 
                    type="button" 
                    @click="openReassignModal(imeiItem)"
                    class="inline-flex items-center justify-center p-1.5 text-indigo-600 hover:text-indigo-900 bg-indigo-50 hover:bg-indigo-100 rounded transition-colors"
                    title="Reasignar Tienda / Depósito"
                  >
                    <i class="fa fa-exchange-alt"></i>
                  </button>
                  <button 
                    type="button" 
                    @click="openEditImeiModal(imeiItem)"
                    class="inline-flex items-center justify-center p-1.5 text-blue-600 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 rounded transition-colors"
                    title="Editar IMEI"
                  >
                    <i class="fa fa-pencil-alt"></i>
                  </button>
                  <button 
                    v-if="imeiItem.status === 'available'"
                    type="button" 
                    @click="deleteImei(imeiItem)"
                    class="inline-flex items-center justify-center p-1.5 text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 rounded transition-colors"
                    title="Eliminar IMEI"
                  >
                    <i class="fa fa-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="!product.imeis || product.imeis.length === 0">
              <td colspan="6" class="py-6 px-6 text-center text-gray-400 text-sm">
                No hay seriales/IMEIs registrados para este producto.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- MODAL: AGREGAR REFERENCIA CRUZADA -->
    <div v-if="crossReferenceModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div class="relative w-full max-w-lg bg-white rounded-xl shadow-xl">
        <div class="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 class="text-lg font-bold text-gray-900 flex items-center gap-2">
            <i class="fa fa-plus-circle text-indigo-600"></i>
            <span>Nueva Referencia Cruzada</span>
          </h3>
          <button @click="closeCrossReferenceModal" type="button" class="text-gray-400 hover:text-gray-700 rounded-lg p-1">
            <i class="fa fa-times"></i>
          </button>
        </div>

        <form @submit.prevent="submitCrossReference" class="p-6 space-y-4">
          <div>
            <label class="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
              Código alterno o número de parte *
            </label>
            <input
              v-model="crossReferenceForm.code"
              type="text"
              class="form-input w-full rounded-lg border-gray-300 font-mono text-sm focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="ej. 90915-YZZE1, OEM-234, PH8A..."
              required
            />
            <div v-if="crossReferenceForm.errors.code" class="text-red-500 text-xs mt-1">
              {{ crossReferenceForm.errors.code }}
            </div>
          </div>

          <div>
            <label class="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
              Marca o Fabricante de Referencia
            </label>
            <input
              v-model="crossReferenceForm.brand"
              type="text"
              class="form-input w-full rounded-lg border-gray-300 text-sm focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="ej. Toyota OEM, Denso, Fram, Bosch..."
            />
          </div>

          <div>
            <label class="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
              Vincular con otro producto del catálogo (opcional)
            </label>
            <select
              v-model="crossReferenceForm.cross_product_id"
              class="form-select w-full rounded-lg border-gray-300 text-sm focus:border-indigo-500 focus:ring-indigo-500"
            >
              <option :value="null">Ninguno (solo código alterno externo)</option>
              <option 
                v-for="p in allProducts" 
                :key="p.id" 
                :value="p.id"
              >
                {{ p.name }} ({{ p.code }})
              </option>
            </select>
          </div>

          <div>
            <label class="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
              Descripción o notas de compatibilidad
            </label>
            <textarea
              v-model="crossReferenceForm.description"
              rows="3"
              class="form-textarea w-full rounded-lg border-gray-300 text-sm focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="Notas sobre compatibilidad, motor, posición, reemplazo..."
            ></textarea>
          </div>

          <div class="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              @click="closeCrossReferenceModal"
              class="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Cancelar
            </button>
            <loading-button
              :loading="crossReferenceForm.processing"
              type="submit"
              class="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg text-sm"
            >
              Guardar Referencia
            </loading-button>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal Asignar IMEIs Faltantes -->
    <div v-if="missingImeisModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div class="relative w-full max-w-2xl max-h-full flex flex-col bg-white rounded-lg shadow dark:bg-gray-700">
        <div class="flex items-center justify-between p-4 border-b rounded-t dark:border-gray-600 shrink-0">
          <h3 class="text-xl font-semibold text-gray-900 dark:text-white">
            Asignar IMEIs Faltantes en {{ activeMissingDeposit?.deposit?.name }}
          </h3>
          <button @click="closeMissingImeisModal" type="button" class="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <form @submit.prevent="submitMissingImeis" class="p-4 md:p-5 flex-1 overflow-hidden flex flex-col">
          <p class="text-sm text-gray-600 mb-4 shrink-0">
            Por favor, ingrese los <strong>{{ activeMissingDeposit?.missing }}</strong> IMEIs faltantes para completar el inventario registrado en este depósito.
          </p>
          
          <div class="space-y-4 overflow-y-auto flex-1 pr-2">
            <div v-for="(imei, index) in missingImeisForm.imeis" :key="index">
              <div class="flex justify-between items-center mb-1">
                <label :for="'imei-'+index" class="block text-sm font-medium text-gray-900">
                  IMEI / Serial #{{ index + 1 }}
                </label>
                <span v-if="missingImeisForm.imeis[index] && !/^\d{15}$/.test(missingImeisForm.imeis[index].trim())" class="text-xs text-red-500 font-medium">
                  {{ missingImeisForm.imeis[index].trim().length }} / 15 dígitos
                </span>
                <span v-else-if="missingImeisForm.imeis[index] && /^\d{15}$/.test(missingImeisForm.imeis[index].trim())" class="text-xs text-green-600 font-semibold flex items-center gap-1">
                  <i class="fa fa-check"></i> Válido (15 dígitos)
                </span>
              </div>
              <input 
                type="text" 
                :id="'imei-'+index" 
                v-model="missingImeisForm.imeis[index]"
                maxlength="15"
                placeholder="Ingrese el IMEI de 15 dígitos"
                class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 font-mono" 
                required
              >
              <div v-if="missingImeisForm.errors[`imeis.${index}`]" class="text-red-500 text-xs mt-1">
                {{ missingImeisForm.errors[`imeis.${index}`] }}
              </div>
            </div>
          </div>
          
          <div class="flex items-center gap-3 pt-4 border-t border-gray-200 mt-4 shrink-0">
            <loading-button 
              :loading="missingImeisForm.processing"
              type="submit" 
              class="btn-blue w-full sm:w-auto"
            >
              Guardar IMEIs
            </loading-button>
            <button 
              type="button" 
              @click="closeMissingImeisModal"
              class="text-gray-500 bg-white hover:bg-gray-100 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5"
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal Reasignar Tienda / Depósito -->
    <div v-if="reassignModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div class="relative w-full max-w-md bg-white rounded-lg shadow">
        <div class="flex items-center justify-between p-4 border-b">
          <h3 class="text-lg font-bold text-gray-900 flex items-center gap-2">
            <i class="fa fa-exchange-alt text-indigo-600"></i>
            <span>Reasignar Tienda de IMEI</span>
          </h3>
          <button @click="closeReassignModal" type="button" class="text-gray-400 hover:text-gray-900 rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <form @submit.prevent="submitReassign" class="p-6">
          <div class="mb-4">
            <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">IMEI Seleccionado</label>
            <div class="font-mono font-bold text-indigo-900 text-base bg-gray-50 p-2.5 rounded border border-gray-200">
              {{ activeImei?.imei }}
            </div>
            <div class="text-xs text-gray-500 mt-1">
              Tienda actual: <span class="font-semibold">{{ activeImei?.deposit?.name || 'Sin Tienda' }}</span>
            </div>
          </div>

          <div class="mb-6">
            <label for="reassign-deposit" class="block text-sm font-medium text-gray-900 mb-2">
              Nueva Tienda / Depósito destino:
            </label>
            <select
              id="reassign-deposit"
              v-model="reassignForm.deposit_id"
              class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-indigo-500 focus:border-indigo-500 block w-full p-2.5"
              required
            >
              <option :value="null" disabled>Seleccione una tienda...</option>
              <option v-for="dep in deposits" :key="dep.id" :value="dep.id">
                {{ dep.name }}
              </option>
            </select>
            <div v-if="reassignForm.errors.deposit_id" class="text-red-500 text-xs mt-1">
              {{ reassignForm.errors.deposit_id }}
            </div>
          </div>

          <div class="flex items-center gap-3 pt-4 border-t border-gray-200">
            <loading-button 
              :loading="reassignForm.processing"
              type="submit" 
              class="btn-indigo"
            >
              Reasignar Tienda
            </loading-button>
            <button 
              type="button" 
              @click="closeReassignModal"
              class="text-gray-500 bg-white hover:bg-gray-100 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5"
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal Editar IMEI -->
    <div v-if="editImeiModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div class="relative w-full max-w-md bg-white rounded-lg shadow">
        <div class="flex items-center justify-between p-4 border-b">
          <h3 class="text-lg font-bold text-gray-900 flex items-center gap-2">
            <i class="fa fa-pencil-alt text-blue-600"></i>
            <span>Editar Número de IMEI</span>
          </h3>
          <button @click="closeEditImeiModal" type="button" class="text-gray-400 hover:text-gray-900 rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <form @submit.prevent="submitEditImei" class="p-6">
          <div class="mb-6">
            <div class="flex justify-between items-center mb-2">
              <label for="edit-imei-input" class="block text-sm font-medium text-gray-900">
                Número de IMEI / Serial:
              </label>
              <span v-if="editImeiForm.imei && !/^\d{15}$/.test(editImeiForm.imei.trim())" class="text-xs text-red-500 font-medium">
                {{ editImeiForm.imei.trim().length }} / 15 dígitos
              </span>
              <span v-else-if="editImeiForm.imei && /^\d{15}$/.test(editImeiForm.imei.trim())" class="text-xs text-green-600 font-semibold flex items-center gap-1">
                <i class="fa fa-check"></i> Válido (15 dígitos)
              </span>
            </div>
            <input 
              id="edit-imei-input"
              type="text" 
              maxlength="15"
              placeholder="Ingrese el IMEI de 15 dígitos"
              v-model="editImeiForm.imei"
              class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 font-mono" 
              required
            >
            <div v-if="editImeiForm.errors.imei" class="text-red-500 text-xs mt-1">
              {{ editImeiForm.errors.imei }}
            </div>
          </div>

          <div class="flex items-center gap-3 pt-4 border-t border-gray-200">
            <loading-button 
              :loading="editImeiForm.processing"
              type="submit" 
              class="btn-blue"
            >
              Guardar Cambios
            </loading-button>
            <button 
              type="button" 
              @click="closeEditImeiModal"
              class="text-gray-500 bg-white hover:bg-gray-100 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5"
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal Código de Barras / Tickera -->
    <div 
      v-if="showBarcodeModal" 
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      @click.self="closeBarcodeModal"
    >
      <div class="bg-white rounded-2xl shadow-2xl border border-gray-100 max-w-md w-full overflow-hidden transition-all">
        <!-- Modal Header -->
        <div class="px-6 py-4 bg-gradient-to-r from-slate-900 to-indigo-950 text-white flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div class="w-9 h-9 rounded-lg bg-indigo-500/30 flex items-center justify-center text-indigo-300">
              <i class="fa fa-barcode text-lg"></i>
            </div>
            <div>
              <h3 class="font-bold text-base leading-tight">Código de Barras & Tickera</h3>
              <p class="text-xs text-indigo-200">Formato universal para rollos e impresoras térmicas</p>
            </div>
          </div>
          <button 
            type="button"
            @click="closeBarcodeModal" 
            class="text-gray-400 hover:text-white p-1 rounded-md transition-colors"
          >
            <i class="fa fa-times text-lg"></i>
          </button>
        </div>

        <!-- Modal Body -->
        <div class="p-6 space-y-5">
          <!-- Vista previa del código de barras -->
          <div class="bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl p-4 flex flex-col items-center justify-center">
            <span class="text-xs font-semibold text-slate-600 mb-1 truncate max-w-full text-center">
              {{ form.name }}
            </span>
            
            <img 
              v-if="product.barcode" 
              :src="product.barcode" 
              :alt="form.code" 
              class="max-h-16 my-2 object-contain"
            />
            <div v-else class="text-xs text-gray-400 my-4">Cargando código de barras...</div>

            <span class="font-mono font-bold tracking-widest text-slate-800 text-sm">
              * {{ product.clean_code || form.code }} *
            </span>
          </div>

          <!-- Opciones de Impresión -->
          <div class="space-y-3">
            <div class="grid grid-cols-2 gap-3">
              <!-- Formato / Ancho -->
              <div>
                <label class="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                  Tamaño de Papel
                </label>
                <select 
                  v-model="barcodeSettings.size" 
                  class="w-full text-xs font-medium border-gray-300 rounded-lg shadow-sm focus:border-indigo-500 focus:ring-indigo-500 py-2"
                >
                  <option value="80mm">80 mm (POS Estándar)</option>
                  <option value="58mm">58 mm (Mini Tickera)</option>
                  <option value="sticker">Etiqueta (50x30 mm)</option>
                </select>
              </div>

              <!-- Número de Copias -->
              <div>
                <label class="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                  Copias
                </label>
                <input 
                  type="number" 
                  v-model.number="barcodeSettings.copies" 
                  min="1" 
                  max="100" 
                  class="w-full text-xs font-medium border-gray-300 rounded-lg shadow-sm focus:border-indigo-500 focus:ring-indigo-500 py-2"
                />
              </div>
            </div>

            <!-- Interruptores -->
            <div class="flex items-center justify-between pt-1">
              <label class="flex items-center gap-2 text-xs font-medium text-gray-700 cursor-pointer">
                <input 
                  type="checkbox" 
                  v-model="barcodeSettings.showPrice" 
                  class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span>Mostrar Precio</span>
              </label>

              <label class="flex items-center gap-2 text-xs font-medium text-gray-700 cursor-pointer">
                <input 
                  type="checkbox" 
                  v-model="barcodeSettings.showExchange" 
                  class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span>Mostrar Bs</span>
              </label>
            </div>
          </div>
        </div>

        <!-- Modal Footer con Acciones -->
        <div class="px-6 py-4 bg-gray-50 border-t border-gray-100 flex flex-col gap-2.5">
          <button 
            type="button"
            @click="printBarcodeThermal" 
            class="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-teal-700 hover:bg-teal-800 text-white font-bold text-sm rounded-xl shadow-md shadow-teal-700/20 transition-all"
          >
            <i class="fa fa-print"></i>
            <span>Imprimir en Tickera (Web)</span>
          </button>

          <div class="grid grid-cols-2 gap-2">
            <button 
              type="button"
              @click="downloadEscpos" 
              class="flex items-center justify-center gap-1.5 py-2 px-3 bg-slate-200 hover:bg-slate-300 text-slate-800 font-semibold text-xs rounded-lg transition-colors"
              title="Descargar archivo binario con comandos ESC/POS para tickeras"
            >
              <i class="fa fa-download"></i>
              <span>Descargar ESC/POS</span>
            </button>

            <a 
              :href="rawbtUrl" 
              class="flex items-center justify-center gap-1.5 py-2 px-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs rounded-lg transition-colors text-center"
              title="Imprimir directamente en Android vía Bluetooth con la app RawBT"
            >
              <i class="fa fa-mobile-alt"></i>
              <span>Imprimir RawBT</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import FileInput from '@/Shared/FileInput'

export default {
  name: 'ProductEdit',

  components: {
    Head,
    Link,
    LoadingButton,
    SelectInput,
    TextInput,
    FileInput
  },

  layout: Layout,

  remember: 'form',

  props: {
    product: Object,
    groups: Object,
    brands: Object,
    parts: Object,
    productCategories: Object,
    deposits: Array,
    history: Object,
    allProducts: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      activeTab: 'general',
      productPlans: [],
      form: this.$inertia.form({
        _method: 'put',
        code: this.product.code,
        id: this.product.id,
        name: this.product.name,
        line_name: this.product.line_name,
        description: this.product.description,
        volumen: this.product.volumen,
        weight: this.product.weight,
        min_stock: this.product.min_stock || 0,
        max_stock: this.product.max_stock || 0,
        tags: this.product.tags || '',
        prices: this.groups.map(g => {
          const priceObj = this.product?.prices?.find(p => p.group_id === g.id);
          return priceObj ? priceObj.price : 0;
        }),
        brand_id: this.product.brand_id,
        part_id: this.product.part_id,
        alias: this.product.alias,
        contable: this.product.contable,
        has_imei: Boolean(this.product.has_imei),
        require_note: !!this.product.require_note,
        cost: this.product.cost,
        photo: null,
        product_category_id: this.product.product_category_id,
        commission_type: this.product.commission_type || '',
        commission_value: this.product.commission_value || 0
      }),
      historyFilterType: '',
      historySearch: '',
      historyFilterButtons: [
        { label: 'Todos', value: '' },
        { label: 'Ventas', value: 'sale' },
        { label: 'Compras', value: 'purchase' },
        { label: 'Entradas Inv.', value: 'inventory_in' },
        { label: 'Retiros Inv.', value: 'inventory_out' },
        { label: 'Traslados', value: 'transfer' },
      ],
      crossReferenceModal: false,
      crossReferenceForm: this.$inertia.form({
        code: '',
        brand: '',
        description: '',
        cross_product_id: null
      }),
      missingImeisModal: false,
      activeMissingDeposit: null,
      missingImeisForm: this.$inertia.form({
        deposit_id: null,
        imeis: []
      }),
      reassignModal: false,
      editImeiModal: false,
      activeImei: null,
      reassignForm: this.$inertia.form({
        deposit_id: null,
      }),
      editImeiForm: this.$inertia.form({
        imei: '',
      }),
      showBarcodeModal: false,
      barcodeSettings: {
        size: '80mm',
        copies: 1,
        showPrice: true,
        showExchange: true,
      },
    }
  },

  computed: {
    rawbtUrl() {
      const escposUrl = `/products/${this.product.id}/barcode-escpos`;
      return `rawbt:data:application/octet-stream;url=${encodeURIComponent(window.location.origin + escposUrl)}`;
    },

    filteredHistoryEvents() {
      let events = this.history?.events || []

      if (this.historyFilterType) {
        events = events.filter(e => e.type === this.historyFilterType)
      }

      if (this.historySearch) {
        const term = this.historySearch.toLowerCase()
        events = events.filter(e => {
          return (e.entity_name && e.entity_name.toLowerCase().includes(term)) ||
                 (e.description && e.description.toLowerCase().includes(term)) ||
                 (e.deposit_name && e.deposit_name.toLowerCase().includes(term)) ||
                 (e.user_name && e.user_name.toLowerCase().includes(term)) ||
                 (e.entity_detail && e.entity_detail.toLowerCase().includes(term))
        })
      }

      return events
    },

    missingImeisPerDeposit() {
      if (!this.product.has_imei || !this.product.deposit_inventory) return [];
      
      const groupedInventory = {};
      this.product.deposit_inventory.forEach(inv => {
        if (!groupedInventory[inv.deposit_id]) {
          groupedInventory[inv.deposit_id] = {
            deposit_id: inv.deposit_id,
            deposit: inv.deposit || (this.deposits ? this.deposits.find(d => d.id === inv.deposit_id) : { name: 'Desconocido' }),
            quantity: 0
          };
        }
        groupedInventory[inv.deposit_id].quantity += inv.quantity;
      });
      
      return Object.values(groupedInventory).map(inv => {
        const availableImeis = (this.product.imeis || []).filter(i => i.deposit_id === inv.deposit_id && i.status === 'available').length;
        const missing = inv.quantity - availableImeis;
        return {
          deposit: inv.deposit,
          quantity: inv.quantity,
          availableImeis: availableImeis,
          missing: missing > 0 ? missing : 0
        };
      }).filter(item => item.quantity > 0 && item.missing > 0);
    }
  },

  created() {
    if (this.isPlanProduct(this.product.code)) {
      this.initProductPlans()
    }
  },

  methods: {
    cleanCode(code) {
      return (code || '').replace(/[\s\-_.]/g, '').toUpperCase()
    },

    isPlanProduct(code) {
      const c = this.cleanCode(code)
      return c === 'RECARGAPLANES' || c === 'RECARGA2'
    },

    initProductPlans() {
      if (this.product?.description) {
        try {
          const parsed = typeof this.product.description === 'string' ? JSON.parse(this.product.description) : this.product.description
          if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].name) {
            this.productPlans = parsed.map(p => ({
              name: p.name,
              price: Number(p.price || 0)
            }))
            return
          }
        } catch (e) {}
      }

      const c = this.cleanCode(this.product.code)
      const prices = this.product?.prices || []
      const p1 = (prices && prices[0] && parseFloat(prices[0].price) > 0) ? parseFloat(prices[0].price) : null
      const p2 = (prices && prices[1] && parseFloat(prices[1].price) > 0) ? parseFloat(prices[1].price) : null
      const p3 = (prices && prices[2] && parseFloat(prices[2].price) > 0) ? parseFloat(prices[2].price) : null

      if (c === 'RECARGAPLANES') {
        this.productPlans = [
          { name: 'Plan 6GB', price: p1 !== null ? p1 : 5500 },
          { name: 'Plan 12GB', price: p2 !== null ? p2 : 10000 },
          { name: 'Plan 30GB', price: p3 !== null ? p3 : 20000 },
        ]
      } else if (c === 'RECARGA2') {
        this.productPlans = [
          { name: 'Plan 6GB', price: p1 !== null ? p1 : 300 },
          { name: 'Plan 12GB', price: p2 !== null ? p2 : 500 },
          { name: 'Plan 30GB', price: p3 !== null ? p3 : 1000 },
        ]
      } else {
        this.productPlans = []
      }
    },

    addProductPlanRow() {
      this.productPlans.push({
        name: `Plan ${this.productPlans.length + 1}`,
        price: 0
      })
    },

    removeProductPlanRow(idx) {
      if (this.productPlans.length <= 1) {
        return alert('Debe haber al menos un plan configurado.')
      }
      this.productPlans.splice(idx, 1)
    },

    back() {
      window.history.back()
    },

    update() {
      if (this.isPlanProduct(this.form.code)) {
        this.form.description = JSON.stringify(this.productPlans)
        this.productPlans.forEach((plan, idx) => {
          if (this.form.prices[idx] !== undefined) {
            this.form.prices[idx] = Number(plan.price || 0)
          }
        })
      }
      this.form.post(`/products/${this.form.id}`)
    },

    destroy() {
      if (confirm('¿Está seguro que desea eliminar este producto?')) {
        this.$inertia.delete(`/products/${this.product.id}`)
      }
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('es-VE', {
        style: 'currency',
        currency: 'USD'
      }).format(value || 0)
    },

    // Referencias Cruzadas
    openCrossReferenceModal() {
      this.crossReferenceForm.reset()
      this.crossReferenceForm.clearErrors()
      this.crossReferenceModal = true
    },

    closeCrossReferenceModal() {
      this.crossReferenceModal = false
      this.crossReferenceForm.reset()
      this.crossReferenceForm.clearErrors()
    },

    submitCrossReference() {
      this.crossReferenceForm.post(`/products/${this.product.id}/cross-references`, {
        preserveScroll: true,
        onSuccess: () => {
          this.closeCrossReferenceModal()
        }
      })
    },

    deleteCrossReference(ref) {
      if (confirm(`¿Eliminar la referencia cruzada "${ref.code}"?`)) {
        this.$inertia.delete(`/products/${this.product.id}/cross-references/${ref.id}`, {
          preserveScroll: true
        })
      }
    },

    // IMEIs
    openMissingImeisModal(item) {
      this.activeMissingDeposit = item;
      this.missingImeisForm.deposit_id = item.deposit.id;
      this.missingImeisForm.imeis = Array(item.missing).fill('');
      this.missingImeisModal = true;
    },
    
    closeMissingImeisModal() {
      this.missingImeisModal = false;
      this.activeMissingDeposit = null;
      this.missingImeisForm.reset();
      this.missingImeisForm.clearErrors();
    },

    submitMissingImeis() {
      const filledImeis = this.missingImeisForm.imeis.map(i => (i || '').trim());
      const invalidImeis = filledImeis.filter(i => !/^\d{15}$/.test(i));
      if (invalidImeis.length > 0) {
        alert(`Los siguientes códigos no son válidos: ${invalidImeis.join(', ')}. Los IMEIs deben tener exactamente 15 dígitos numéricos.`);
        return;
      }
      this.missingImeisForm.post(`/products/${this.product.id}/add-imeis`, {
        preserveScroll: true,
        onSuccess: () => {
          this.closeMissingImeisModal();
        }
      });
    },

    openReassignModal(item) {
      this.activeImei = item;
      this.reassignForm.deposit_id = item.deposit_id || null;
      this.reassignForm.clearErrors();
      this.reassignModal = true;
    },

    closeReassignModal() {
      this.reassignModal = false;
      this.activeImei = null;
      this.reassignForm.reset();
      this.reassignForm.clearErrors();
    },

    submitReassign() {
      if (!this.activeImei) return;
      this.reassignForm.post(`/products/${this.product.id}/imeis/${this.activeImei.id}/reassign`, {
        preserveScroll: true,
        onSuccess: () => {
          this.closeReassignModal();
        }
      });
    },

    openEditImeiModal(item) {
      this.activeImei = item;
      this.editImeiForm.imei = item.imei;
      this.editImeiForm.clearErrors();
      this.editImeiModal = true;
    },

    closeEditImeiModal() {
      this.editImeiModal = false;
      this.activeImei = null;
      this.editImeiForm.reset();
      this.editImeiForm.clearErrors();
    },

    submitEditImei() {
      if (!this.activeImei) return;
      const imei = (this.editImeiForm.imei || '').trim();
      if (!/^\d{15}$/.test(imei)) {
        alert(`El IMEI ingresado "${imei}" no es válido. Debe tener exactamente 15 dígitos numéricos (actual: ${imei.length}).`);
        return;
      }
      this.editImeiForm.put(`/products/${this.product.id}/imeis/${this.activeImei.id}`, {
        preserveScroll: true,
        onSuccess: () => {
          this.closeEditImeiModal();
        }
      });
    },

    deleteImei(item) {
      if (confirm(`¿Está seguro que desea eliminar el IMEI "${item.imei}" de este producto?`)) {
        this.$inertia.delete(`/products/${this.product.id}/imeis/${item.id}`, {
          preserveScroll: true,
        });
      }
    },

    openBarcodeModal() {
      this.showBarcodeModal = true;
    },

    closeBarcodeModal() {
      this.showBarcodeModal = false;
    },

    printBarcodeThermal() {
      const p = this.barcodeSettings;
      const url = `/products/${this.product.id}/barcode-ticket?size=${p.size}&copies=${p.copies}&show_price=${p.showPrice ? 1 : 0}&show_exchange=${p.showExchange ? 1 : 0}&autoprint=1`;
      window.open(url, 'Ticket Termico', 'width=460,height=680,toolbar=no,menubar=no,location=no,status=no');
    },

    downloadEscpos() {
      window.location.href = `/products/${this.product.id}/barcode-escpos`;
    },
  },
}
</script>
